import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Eye, 
  Search,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle
} from "lucide-react";

const AdminAgenzieList = ({ agenzie, loadingImpersonation }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Filtra agenzie in base a ricerca e filtro stato
  const filteredAgenzie = agenzie.filter(agenzia => {
    const matchesSearch = agenzia.nome_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agenzia.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || agenzia.stato === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calcolo paginazione
  const totalPages = Math.ceil(filteredAgenzie.length / PAGE_SIZE);
  const paginatedAgenzie = filteredAgenzie.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getStatusIcon = (stato) => {
    switch (stato) {
      case 'attiva': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_revisione': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'sospesa': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'disattivata': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (stato) => {
    switch (stato) {
      case 'attiva': return 'bg-green-100 text-green-800';
      case 'in_revisione': return 'bg-yellow-100 text-yellow-800';
      case 'sospesa': return 'bg-orange-100 text-orange-800';
      case 'disattivata': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Importo i componenti di paginazione
  // eslint-disable-next-line
  const { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } = require("./ui/pagination.jsx");

  const handleImpersonateAgenzia = async (id, nome) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const adminToken = localStorage.getItem('admin_token');
      
      // Chiamata API per impersonificazione
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': adminToken ? `Bearer ${adminToken}` : ''
        },
        body: JSON.stringify({
          user_type: 'agenzia',
          user_id: id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Errore ${response.status}`);
      }
      
      const data = await response.json();
      if (data.impersonated_token || data.session) {
        if (data.impersonated_token) {
          localStorage.setItem('impersonated_token', data.impersonated_token);
        }
        const sessionId = data.session?.session_id || data.session_id || '';
        window.location.href = `/dashboard-agenzia?agenzia_id=${id}${sessionId ? `&session=${sessionId}` : ''}`;
      } else {
        // Fallback: apri direttamente la dashboard
        window.location.href = `/dashboard-agenzia?agenzia_id=${id}`;
      }
    } catch (err) {
      console.error('Errore impersonificazione:', err);
      // Fallback: prova ad aprire comunque la dashboard
      if (window.confirm(`Impersonificazione non riuscita: ${err.message}\n\nVuoi provare ad accedere comunque alla dashboard?`)) {
        window.location.href = `/dashboard-agenzia?agenzia_id=${id}`;
      }
    }
  };

  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-blue-600" />
            Gestione Agenzie
            <Badge variant="outline" className="ml-2">
              {filteredAgenzie.length} di {agenzie.length}
            </Badge>
          </CardTitle>
        </div>
        {/* Filtri e Ricerca */}
        <div className="flex gap-4 mt-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Cerca per nome o email..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="pl-10 rounded-xl border-gray-200"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          >
            <option value="all">Tutti gli stati</option>
            <option value="attiva">Attiva</option>
            <option value="in_revisione">In Revisione</option>
            <option value="sospesa">Sospesa</option>
            <option value="disattivata">Disattivata</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {filteredAgenzie.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {agenzie.length === 0 ? "Nessuna agenzia presente" : "Nessuna agenzia trovata"}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedAgenzie.map((agenzia) => (
                <div
                  key={agenzia.id}
                  className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      {/* Info Agenzia */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {agenzia.nome_completo || "SantoloToscano"}
                          </h3>
                          <Badge className={getStatusColor(agenzia.stato)}>
                            {getStatusIcon(agenzia.stato)}
                            <span className="ml-1 capitalize">{agenzia.stato}</span>
                          </Badge>
                          {agenzia.tipo_agenzia && (
                            <Badge variant="outline" className="text-purple-600 border-purple-300">
                              {agenzia.tipo_agenzia}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{agenzia.email || "Email non disponibile"}</span>
                          </div>
                          {agenzia.telefono && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span>{agenzia.telefono}</span>
                            </div>
                          )}
                          {agenzia.zona && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{agenzia.zona}</span>
                            </div>
                          )}
                        </div>
                        {agenzia.data_creazione && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                            <Calendar className="w-3 h-3" />
                            <span>
                              Registrata: {new Date(agenzia.data_creazione).toLocaleDateString('it-IT')}
                            </span>
                            {agenzia.ultimo_accesso && agenzia.ultimo_accesso !== "Non disponibile" && (
                              <span className="ml-4">
                                Ultimo accesso: {new Date(agenzia.ultimo_accesso).toLocaleDateString('it-IT')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Azioni */}
                    <div className="flex items-center gap-2 mt-3 sm:mt-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImpersonateAgenzia(agenzia.id, agenzia.nome_completo)}
                        disabled={loadingImpersonation}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:text-accent-foreground h-8 rounded-md px-3 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Entra nella Dashboard
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => (
                      <PaginationItem key={i}>
                        <PaginationLink
                          isActive={page === i + 1}
                          onClick={() => setPage(i + 1)}
                        >
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Modal centrato verticalmente per agenzia
function SimpleAgencyModal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl relative">
        <button
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors text-xl font-bold"
          onClick={onClose}
          aria-label="Chiudi"
        >
          ×
        </button>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

/*
Esempio di utilizzo (aggiungi dove serve, ad esempio sotto la lista agenzie):
// Modal dettagli agenzia
// {showAgencyModal && selectedAgency && (
//   <SimpleAgencyModal open={showAgencyModal} onClose={() => setShowAgencyModal(false)}>
//     <h2 className="text-xl font-bold mb-2">Scheda Agenzia</h2>
//     <div className="space-y-2">
//       <div><strong>Nome:</strong> {selectedAgency.nome_completo}</div>
//       <div><strong>Email:</strong> {selectedAgency.email}</div>
//       <div><strong>Telefono:</strong> {selectedAgency.telefono}</div>
//       // ...altri dettagli...
//     </div>
//   </SimpleAgencyModal>
// )}
*/

export default AdminAgenzieList;